import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { History, Lock, Users } from 'lucide-react';
import { Button, Card, EmptyState, PageHeader, Spinner } from '../../components/ui';
import { Badge } from '../../components/badges';
import { useCurrentUser } from '../../api/auth';
import { useGroups, useSessions } from '../../api/queries';
import type { Session } from '../../api/types';
import { formatDayShort, toIsoDate } from '../../lib/format';

/**
 * ლექტორის სამუშაო ეკრანი.
 *
 * აღრიცხვა **მხოლოდ გაკვეთილის საკუთარ დღეს** იხსნება. მომდევნო გაკვეთილი
 * აქვე ჩანს, რომ ლექტორმა წინასწარ იცოდეს, მაგრამ ჩაკეტილია.
 * მერე გაკვეთილები აქ არ ჩანს — ისინი განრიგშია. ქვემოთ ჩატარებულების ისტორიაა.
 */
export function JournalPage() {
  const user = useCurrentUser();
  const { data: groups, isLoading: groupsLoading } = useGroups({ pageSize: 100 });
  const { data: sessions, isLoading: sessionsLoading } = useSessions({ limit: 500 });

  const today = toIsoDate(new Date());

  const { current, past } = useMemo(() => {
    const all = (sessions ?? []).filter((s) => s.status !== 'CANCELLED');

    // ჩანს: დღევანდელი (ღია) + ჯგუფზე ერთი მომდევნო (ჩაკეტილი)
    const current = all
      .filter((s) => {
        const date = s.date.slice(0, 10);
        if (date === today) return true;
        if (date <= today) return false;
        // მომდევნო — ჯგუფში ყველაზე ადრეული დღევანდელის შემდეგ
        const earliestNext = all
          .filter((x) => x.group.id === s.group.id && x.date.slice(0, 10) > today)
          .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime))[0];
        return earliestNext?.id === s.id;
      })
      .sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime));

    const past = all
      .filter((s) => s.date.slice(0, 10) < today)
      .sort((a, b) => b.date.localeCompare(a.date));

    return { current, past };
  }, [sessions, today]);

  if (groupsLoading || sessionsLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-6" />
      </div>
    );
  }

  const myGroups = groups?.items ?? [];

  if (myGroups.length === 0) {
    return (
      <>
        <PageHeader title={user.role === 'LECTURER' ? 'აღრიცხვა' : 'ჟურნალი'} />
        <div className="rounded-[10px] border border-line bg-surface shadow-xs">
          <EmptyState icon={<Users className="size-8" />} title="ჯგუფები მიმაგრებული არ არის" />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title={user.role === 'LECTURER' ? 'აღრიცხვა' : 'ჟურნალი'} />

      <div className="space-y-6">
        {/* --- აღსარიცხი: დღევანდელი + ერთი მომდევნო --- */}
        {current.length === 0 ? (
          <div className="rounded-[10px] border border-line bg-surface shadow-xs">
            <EmptyState title="აღსარიცხი გაკვეთილი არ არის" />
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {current.map((s) => (
              <CurrentCard key={s.id} session={s} today={today} />
            ))}
          </div>
        )}

        {/* --- ჩატარებული: მხოლოდ სანახავად --- */}
        {past.length > 0 && (
          <Card
            title={
              <span className="flex items-center gap-2">
                <History className="size-4 text-ink-mute" />
                ჩატარებული
              </span>
            }
          >
            <ul className="divide-y divide-line">
              {past.slice(0, 12).map((s) => (
                <PastRow key={s.id} session={s} />
              ))}
            </ul>
          </Card>
        )}
      </div>
    </>
  );
}

// ============================================================

/**
 * გაკვეთილის ბარათი. აღრიცხვა მხოლოდ საკუთარ დღეს იხსნება — მომდევნო
 * ჩანს, რომ ლექტორმა წინასწარ იცოდეს, მაგრამ ჩაკეტილია.
 */
function CurrentCard({ session: s, today }: { session: Session; today: string }) {
  const date = s.date.slice(0, 10);
  const isToday = date === today;

  const body = (
    <>
      <div className="mb-1 flex items-start justify-between gap-2">
        <span className="tabular font-semibold tracking-tight text-ink">{s.group.name}</span>
        <Badge tone={isToday ? 'brand' : 'neutral'}>{isToday ? 'დღეს' : 'მომდევნო'}</Badge>
      </div>

      <p className="mb-3 line-clamp-1 text-[13px] text-ink-soft">
        {s.topic ?? s.group.course.name}
      </p>

      <div className="mb-3 flex items-center gap-2 text-[12px] text-ink-mute">
        <span>{formatDayShort(s.date)}</span>
        <span>·</span>
        <span className="tabular">{s.startTime}</span>
      </div>
    </>
  );

  if (!isToday) {
    return (
      <div className="rounded-[10px] border border-line bg-surface p-4 shadow-xs opacity-70">
        {body}
        <p className="flex items-center justify-center gap-1.5 rounded-lg bg-canvas px-3 py-2 text-[12px] text-ink-mute">
          <Lock className="size-3.5" />
          გაიხსნება {formatDayShort(s.date)}
        </p>
      </div>
    );
  }

  return (
    <Link
      to={`/journal/${s.id}`}
      className="block rounded-[10px] border border-line bg-surface p-4 shadow-xs transition hover:border-brand-300 hover:shadow-md"
    >
      {body}
      <Button size="sm" className="w-full">
        აღრიცხვა
      </Button>
    </Link>
  );
}

/** გასული გაკვეთილი — ისტორია, ლექტორი ვეღარ ცვლის */
function PastRow({ session: s }: { session: Session }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
      <span className="w-28 shrink-0 text-[13px] text-ink-soft">{formatDayShort(s.date)}</span>
      <span className="tabular w-12 shrink-0 text-[13px] text-ink-mute">{s.startTime}</span>
      <span className="tabular w-32 shrink-0 text-[13px] font-medium text-ink">{s.group.name}</span>
      <span className="min-w-0 flex-1 truncate text-[13px] text-ink-mute">
        {s.topic ?? s.group.course.name}
      </span>
      <Link
        to={`/journal/${s.id}`}
        className="text-[12px] text-ink-mute underline-offset-2 transition hover:text-ink hover:underline"
      >
        ნახვა
      </Link>
    </li>
  );
}
